import { useEffect, useMemo, useState } from "react";
import { Form } from "@/components/Form";
import { TranslationProvider } from "@/contexts/TranslationProvider";
import { Container } from "@/components/ui/Container";
import { Spinner } from "@/components/ui/Spinner";
import { ConfigProvider } from "@/contexts/ConfigProvider";
import classes from "./App.module.scss";
import { getComponentsTranslations } from "@/utils/translations";
import { useService } from "@/hooks/useService";
import type { FormConfig } from "@/types/FormConfig";
import { AppConfigProvider } from "@/contexts/AppConfigProvider";
import { ErrorFallback } from "@/components/ErrorFallback";
import { dispatchEvent } from "@/utils/event-dispacher";
import { EventName } from "@/types/Events";

function App() {
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { getFormConfig } = useService();

  useEffect(() => {
    getFormConfig()
      .then((data) => {
        setFormConfig(data);
        dispatchEvent(EventName.INIT);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [getFormConfig]);

  const translations = useMemo(
    () => getComponentsTranslations(formConfig?.components),
    [formConfig?.components],
  );

  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <div className={classes.pageCenter}>
          <Spinner />
        </div>
      </Container>
    );
  }

  if (error) {
    return <ErrorFallback />;
  }

  if (!formConfig?.active) return null;

  return (
    <TranslationProvider translations={translations}>
      <AppConfigProvider config={formConfig}>
        <ConfigProvider config={formConfig}>
          <Form id={formConfig.formId} />
        </ConfigProvider>
      </AppConfigProvider>
    </TranslationProvider>
  );
}

export default App;
